import { environment } from '../../../environments/environment';

/** Trailing slash so `api_url + 'path'` concatenates correctly. */
const api_url = environment.apiBaseUrl.endsWith('/') ? environment.apiBaseUrl : `${environment.apiBaseUrl}/`;

export interface ApiConstants {
  [moduleName: string]: {
    [callName: string]: {
      url: string;
      method: string;
    };
  };
}

/**
 * Nested catalogue keyed by module → call for ConfigService.getRestObject.
 * Paths include the base URL so RestService.callServer can use them as-is.
 * Synced from Swagger / OpenAPI (Custodian-Broker Reconciliation Platform API v1)
 * at `https://api.armatech.ai/swagger-ui/index.html` (`/v3/api-docs`).
 * Path params use `:param` placeholders for ConfigService.constructUrl.
 *
 * Authentication: Swagger `bearerAuth` (HTTP Bearer JWT) is required on all
 * catalogue calls except `auth.*` login/OTP/password-reset, `public.*`,
 * and `positionExports.downloadUrlByToken` (emailed export token; no JWT).
 */
export const apiConstants: ApiConstants = {
  auth: {
    /** Log in (step 2 of 2: OTP) */
    verifyOtp: { url: api_url + 'api/v1/auth/verify-otp', method: 'POST' },
    /** Reset/change your own password */
    resetPassword: { url: api_url + 'api/v1/auth/reset-password', method: 'POST' },
    /** Resend the OTP */
    resendOtp: { url: api_url + 'api/v1/auth/resend-otp', method: 'POST' },
    /** Refresh access token */
    refresh: { url: api_url + 'api/v1/auth/refresh', method: 'POST' },
    /** Request a password-reset link (forgot password) */
    requestPasswordReset: { url: api_url + 'api/v1/auth/password-reset/request', method: 'POST' },
    /** Set your password via an emailed reset link */
    confirmPasswordReset: { url: api_url + 'api/v1/auth/password-reset/confirm', method: 'POST' },
    /** Log out (current session) */
    logout: { url: api_url + 'api/v1/auth/logout', method: 'POST' },
    /** Log in (step 1 of 2: credentials) */
    login: { url: api_url + 'api/v1/auth/login', method: 'POST' },
  },
  admin: {
    /** List all client tenants (paginated; query status=ACTIVE|PENDING|INACTIVE|ALL) */
    getAll: { url: api_url + 'api/v1/tenants', method: 'GET' },
    /** Alias kept for callers that still use listAll */
    listAll: { url: api_url + 'api/v1/tenants', method: 'GET' },
    /** Onboard a new client tenant */
    createTenant: { url: api_url + 'api/v1/admin/tenants', method: 'POST' },
    /** Update a client tenant (name/description/classification; PLATFORM_ADMIN) */
    updateTenant: { url: api_url + 'api/v1/admin/tenants/:id', method: 'PATCH' },
    /** Custom processing pipeline toggle + services (PARSING required; RULES/RAG require MATCHING). Applies to jobs accepted after the change. */
    updateTenantPipeline: { url: api_url + 'api/v1/admin/tenants/:id/pipeline', method: 'PUT' },
    /** Blocks login: optional query status = SUSPENDED or DEACTIVATED (default DEACTIVATED). Must be an ACTIVE lk_onboarding_status with can_login=false other than KYB_PENDING. Revokes all refresh tokens and ends all open sessions. The reserved PLATFORM tenant cannot be deactivated. Idempotent if already that status. */
    softDelete: { url: api_url + 'api/v1/admin/tenants/:id', method: 'DELETE' },
    /**Sets a login-allowed onboarding status. Query status omitted: ON_BOARDED when login is blocked, no-op when login already allowed. Explicit status must have can_login=true (e.g. KYB_APPROVED, PILOT, ON_BOARDED). Does not restore prior sessions — users must sign in again. */
    activate: { url: api_url + 'api/v1/admin/tenants/:id/activate', method: 'POST' },
    /**
     * Replace tenant Module Access (PLATFORM_ADMIN).
     * Baseline IAM modules stay locked ACTIVE; omitted product modules are soft-disabled.
     */
    updateModules: { url: api_url + 'api/v1/admin/tenants/:id/modules', method: 'PUT' },
  },
  /**
   * EC2 co-located process census. PLATFORM_ADMIN only.
   * The auth interceptor sends `Authorization: Bearer <access token>`.
   */
  hostServices: {
    /** GET `/api/v1/admin/host-services` — `HostServiceCensusResponse`. */
    census: { url: api_url + 'api/v1/admin/host-services', method: 'GET' },
  },
  tenants: {
    list: { url: api_url + 'api/v1/tenants', method: 'GET' },
    /** GET tenant lifecycle counts — `TenantCountsResponse` (`all`, `pending`, `active`, `inactive`). */
    counts: { url: api_url + 'api/v1/tenants/counts', method: 'GET' },
    /** Get tenant by id */
    getById: { url: api_url + 'api/v1/tenants/:id', method: 'GET' },
    /** Deactivate (soft-delete) the tenant — not listed on current Swagger; prefer `admin.softDelete` */
    softDelete: { url: api_url + 'api/v1/tenants/:id', method: 'DELETE' },
    /** Update tenant details */
    update: { url: api_url + 'api/v1/tenants/:id', method: 'PATCH' },
  },
  /**
   * Tenant Config — Swagger tag `Tenant Config`.
   * Responses: `TenantConfigResponse` / `PagedResponseTenantConfigResponse`.
   * Publish body: `PublishTenantConfigRequest` (required: configJson; optional: description, tenantId).
   */
  tenantConfig: {
    /**
     * GET active config — `TenantConfigResponse`.
     * Query: optional `tenantId` (uuid; required for PLATFORM-scoped TENANT_CONFIG_VIEW).
     * TENANT scope uses the caller's tenant (foreign tenantId rejected).
     * Includes tenantName / tenantCode.
     */
    getActive: { url: api_url + 'api/v1/tenant-config', method: 'GET' },
    /**
     * PUT publish new version — body `PublishTenantConfigRequest` → `TenantConfigResponse`.
     * Inserts version = max+1 as ACTIVE and marks the previous ACTIVE row INACTIVE.
     * PLATFORM-scoped TENANT_CONFIG_MANAGE requires body.tenantId; TENANT scope publishes to caller.
     */
    publish: { url: api_url + 'api/v1/tenant-config', method: 'PUT' },
    /**
     * GET version by id — path `:id` (uuid) → `TenantConfigResponse`.
     * PLATFORM-scoped TENANT_CONFIG_VIEW can read any tenant's version.
     */
    getById: { url: api_url + 'api/v1/tenant-config/:id', method: 'GET' },
    /**
     * GET version history — `PagedResponseTenantConfigResponse`.
     * Query: pageable (`page`, `size`, `sort[]`; default sort `createdAt,desc`),
     * optional `tenantId` (uuid), optional `status` = ACTIVE (default) | INACTIVE | ALL.
     * PLATFORM omit tenantId + status=ACTIVE = live config per tenant that has published.
     */
    listVersions: { url: api_url + 'api/v1/tenant-config/versions', method: 'GET' },
    /**
     * GET tenant-config lifecycle counts — `LifecycleCountsResponse` (`all`, `active`, `inactive`).
     * Same tenant visibility as list versions. active = currently published; inactive = superseded.
     * Query: optional `tenantId` (uuid; PLATFORM-scoped TENANT_CONFIG_VIEW; omit = all tenants).
     */
    counts: { url: api_url + 'api/v1/tenant-config/counts', method: 'GET' },
  },
  /**
   * Tenant Branding — Swagger tag `Tenant Branding`.
   * `tenantId` (query or body) is required for PLATFORM-scoped callers on tenant-specific calls.
   * GET `/me`, `/`, `/draft` return 204 when there is nothing to show.
   */
  tenantBranding: {
    /** GET slim active branding for the signed-in tenant (`TenantBrandingMeResponse`) or 204. */
    me: { url: api_url + 'api/v1/tenant-branding/me', method: 'GET' },
    /** GET active branding for `tenantId` (`TenantBrandingResponse`) or 204. BRANDING_VIEW. */
    getActive: { url: api_url + 'api/v1/tenant-branding', method: 'GET' },
    /** GET draft for `tenantId` or 204. BRANDING_VIEW. */
    getDraft: { url: api_url + 'api/v1/tenant-branding/draft', method: 'GET' },
    /** PUT `SaveBrandingDraftRequest` → `TenantBrandingResponse` (with `warnings`). BRANDING_MANAGE. */
    saveDraft: { url: api_url + 'api/v1/tenant-branding/draft', method: 'PUT' },
    /**
     * POST `BrandingAssetUploadRequest` → `{ uploadUrl, assetUrl, objectKey, contentType, expiresAt }`.
     * Client PUTs file bytes to `uploadUrl`, then stores `assetUrl` in branding JSON. BRANDING_MANAGE.
     */
    createAssetUploadUrl: { url: api_url + 'api/v1/tenant-branding/assets/upload-url', method: 'POST' },
    /** DELETE draft for `tenantId` → 204. BRANDING_MANAGE. */
    discardDraft: { url: api_url + 'api/v1/tenant-branding/draft', method: 'DELETE' },
    /** POST `PublishBrandingRequest` (optional `branding`) → 201 `TenantBrandingResponse`. BRANDING_MANAGE. */
    publish: { url: api_url + 'api/v1/tenant-branding/publish', method: 'POST' },
    /** GET paged history (`status` ACTIVE | INACTIVE | ALL, default sort version,desc). BRANDING_VIEW. */
    listVersions: { url: api_url + 'api/v1/tenant-branding/versions', method: 'GET' },
    /** GET one version or draft by `:id`. BRANDING_VIEW. */
    getById: { url: api_url + 'api/v1/tenant-branding/:id', method: 'GET' },
    /** POST re-publish an INACTIVE `:id` as a new ACTIVE version → 201. BRANDING_MANAGE. */
    rollback: { url: api_url + 'api/v1/tenant-branding/:id/rollback', method: 'POST' },
    /** POST `ResetBrandingRequest` → 204; tenant returns to platform defaults. BRANDING_MANAGE. */
    reset: { url: api_url + 'api/v1/tenant-branding/reset', method: 'POST' },
    /** GET `{ tenants, published, draftPending, platformDefault }`. BRANDING_VIEW. */
    counts: { url: api_url + 'api/v1/tenant-branding/counts', method: 'GET' },
    /** GET paged one-row-per-tenant summary (`state`, `q`, `page`, `size`, `sort`). BRANDING_VIEW. */
    summary: { url: api_url + 'api/v1/tenant-branding/summary', method: 'GET' },
  },
  /**
   * Per-user grid layouts. GET with no row is 200 and `value: null` — the screen
   * keeps its built-in columns. List screens omit `tenantId`.
   */
  uiPreferences: {
    /** GET one layout. Query `key` (e.g. grid.tenants_list). */
    get: { url: api_url + 'api/v1/users/me/ui-preferences', method: 'GET' },
    /** PUT upsert. Path `:key`. Body `{ value: { version, columnState } }`. */
    upsert: { url: api_url + 'api/v1/users/me/ui-preferences/:key', method: 'PUT' },
    /** DELETE the caller's layout for `:key`. 204 when already absent. */
    reset: { url: api_url + 'api/v1/users/me/ui-preferences/:key', method: 'DELETE' },
  },
  users: {
    /** List users (paginated; optional tenantId filter) */
    list: { url: api_url + 'api/v1/users', method: 'GET' },

    counts: { url: api_url + 'api/v1/users/counts', method: 'GET' },
    /** Create a user in the caller's tenant */
    create: { url: api_url + 'api/v1/users', method: 'POST' },
    /** Unlock a user */
    unlock: { url: api_url + 'api/v1/users/:id/unlock', method: 'POST' },
    /** List roles assigned to a user (paginated) */
    listRoles: { url: api_url + 'api/v1/users/:id/roles', method: 'GET' },
    /** Assign a role to a user */
    assignRole: { url: api_url + 'api/v1/users/:id/roles', method: 'POST' },
    /** Activate a deactivated user */
    activate: { url: api_url + 'api/v1/users/:id/activate', method: 'POST' },
    /** Get a user by id */
    getById: { url: api_url + 'api/v1/users/:id', method: 'GET' },
    /** Deactivate (soft-delete) a user */
    softDelete: { url: api_url + 'api/v1/users/:id', method: 'DELETE' },
    /** Update a user */
    update: { url: api_url + 'api/v1/users/:id', method: 'PATCH' },
    /** Remove a role from a user */
    removeRole: { url: api_url + 'api/v1/users/:id/roles/:roleId', method: 'DELETE' },
  },
  roles: {
    /** List roles (paginated; optional tenantId filter) */
    list: { url: api_url + 'api/v1/roles', method: 'GET' },

    counts: { url: api_url + 'api/v1/roles/counts', method: 'GET' },
    /** Create a custom role in the caller's tenant */
    create: { url: api_url + 'api/v1/roles', method: 'POST' },
    /** Activate a role (`status=ACTIVE`). System-provisioned roles cannot be modified. */
    activate: { url: api_url + 'api/v1/roles/:id/activate', method: 'POST' },
    /** List permissions granted to a role (paginated) */
    listPermissions: { url: api_url + 'api/v1/roles/:id/permissions', method: 'GET' },
    /** Grant a permission to a role */
    grantPermission: { url: api_url + 'api/v1/roles/:id/permissions', method: 'POST' },
    /** Get a role by id */
    getById: { url: api_url + 'api/v1/roles/:id', method: 'GET' },
    /** Deactivate (soft-delete) a role */
    softDelete: { url: api_url + 'api/v1/roles/:id', method: 'DELETE' },
    /** Update a role */
    update: { url: api_url + 'api/v1/roles/:id', method: 'PATCH' },
    /** Revoke a permission from a role */
    revokePermission: { url: api_url + 'api/v1/roles/:id/permissions/:permissionId', method: 'DELETE' },
  },
  permissions: {
    /** List the global permission catalog (paginated) */
    list: { url: api_url + 'api/v1/permissions', method: 'GET' },
    /** Get a permission by id */
    getById: { url: api_url + 'api/v1/permissions/:id', method: 'GET' },
  },
  /**
   * Lookups — Swagger tag `Lookups`.
   * Read-only ACTIVE catalog options for dropdowns and checklists.
   * All endpoints require authentication; tenant create/edit lookups are PLATFORM_ADMIN.
   */
  lookups: {
    /** List contract tiers — ACTIVE options for tenant create/edit (PLATFORM_ADMIN). */
    listContractTiers: { url: api_url + 'api/v1/lookups/contract-tiers', method: 'GET' },
    /** List firm types — ACTIVE options for tenant create/edit (PLATFORM_ADMIN). */
    listFirmTypes: { url: api_url + 'api/v1/lookups/firm-types', method: 'GET' },
    /**
     * List match statuses — ACTIVE filter options for position/cash lists.
     * `GET /api/v1/lookups/match-statuses` → `LookupItemResponse[]`
     * (`code`, `displayName`, `description`, `sortOrder`). No query params.
     * Includes `UNMATCHED` as a filter-only option (do not write `UNMATCHED`
     * to `positions.match_status`). Requires RECON_VIEW.
     */
    listMatchStatuses: { url: api_url + 'api/v1/lookups/match-statuses', method: 'GET' },
    /**
     * List modules — ACTIVE catalog modules with permission keys.
     * `baseline=true` rows are the locked IAM list on create-tenant;
     * `baseline=false` rows are the selectable Module Access checklist.
     * Platform-only modules (e.g. APP_LOG, TENANT_CREATE) are omitted.
     */
    listModules: { url: api_url + 'api/v1/lookups/modules', method: 'GET' },
    /**
     * List onboarding statuses — ACTIVE options for tenant create/edit (PLATFORM_ADMIN).
     * Includes `canLogin` and `accessMode` metadata.
     */
    listOnboardingStatuses: { url: api_url + 'api/v1/lookups/onboarding-statuses', method: 'GET' },
    /**
     * List resolution categories for a position match status — ACTIVE Resolve Break dropdown options.
     * Query (required): `matchStatus` = any ACTIVE break match status from the match-statuses catalog
     * (e.g. MISSING_IN_INTERNAL, QTY_MISMATCH). Do not hardcode the allowed codes in the UI —
     * derive them from `lookups.listMatchStatuses` / the selected filter value.
     */
    listResolutionCategories: { url: api_url + 'api/v1/lookups/resolution-categories', method: 'GET' },
    /**
     * Every ACTIVE Resolve Break category for the caller tenant, grouped by match status.
     * One call; the Positions tab reuses the groups instead of requesting each status.
     */
    listResolutionCategoryCatalog: { url: api_url + 'api/v1/lookups/resolution-categories/catalog', method: 'GET' },
    /** List role profiles — ACTIVE options for the Create Role dropdown. */
    listRoleProfiles: { url: api_url + 'api/v1/lookups/role-profiles', method: 'GET' },
  },
  uploads: {
    /** Confirm a successful direct-to-S3 upload (client Step 2) */
    complete: { url: api_url + 'api/v1/uploads/:refId/complete', method: 'POST' },
    /** Issue a pre-signed S3 upload URL for a ZIP package */
    presign: { url: api_url + 'api/v1/uploads/presign', method: 'POST' },
    /** Get upload tracking status by refId */
    getByRefId: { url: api_url + 'api/v1/uploads/:refId', method: 'GET' },
  },
  /**
   * Jobs — Swagger tag `Jobs`.
   * S3-triggered reconciliation pipeline: list/detail, status history, nested files/events/runs/positions,
   * and dev simulation helpers. Responses: `JobResponse` / `PagedResponseJobResponse` and related nested DTOs.
   * TENANT scope = caller's tenant; PLATFORM-scoped JOB_VIEW or RECON_VIEW = cross-tenant (optional `tenantId` on list/counts).
   */
  jobs: {
    /**
     * GET list jobs (paginated) — `PagedResponseJobResponse`.
     * Query: pageable (`page`, `size`, `sort`; default sort `createdAt,desc`),
     * optional `tenantId` (uuid; PLATFORM-scoped JOB_VIEW / RECON_VIEW; omit = all tenants),
     * optional hierarchy filters `client`, `custodian`, `reconType`, `fund` (omit = all; `BLANK` = null/blank),
     * optional `status` = exact pipeline status | ALL | FAILED | IN_PROGRESS | COMPLETED (same buckets as `/counts`).
     * Each row may include tenantName/tenantCode and `file_uploaded_by_id` / `file_uploaded_by` from file_upload.
     */
    list: { url: api_url + 'api/v1/jobs', method: 'GET' },
    /**
     * GET job pipeline-status counts — `JobCountsResponse`
     * (`all`, `failed`, `completed`, `inProgress`, `byStatus`).
     * Same tenant visibility as list. Optional `tenantId` for PLATFORM-scoped JOB_VIEW / RECON_VIEW (omit = all tenants).
     * failed = REJECTED | CANCELLED | PARSING_FAILED | MATCHING_FAILED | RULES_FAILED | RAG_FAILED;
     * inProgress = every status other than failed and COMPLETED; byStatus = per-status breakdown.
     */
    counts: { url: api_url + 'api/v1/jobs/counts', method: 'GET' },
    /**
     * POST simulate an S3 marker-file event (development/testing aid).
     * Stands in for the real S3 → SQS → listener flow; exercises the same job/file creation logic.
     */
    simulateMarker: { url: api_url + 'api/v1/jobs/simulate-marker', method: 'POST' },
    /**
     * POST simulate ZIP package ingestion (development/testing aid).
     * Accepts a local path or s3:// URI, validates manifest/marker/data files, creates job_files and job_events,
     * then transitions to ACCEPTED or REJECTED.
     */
    simulateZip: { url: api_url + 'api/v1/jobs/simulate-zip', method: 'POST' },
    /**
     * GET job by id — `JobResponse`.
     * Path `:id` (uuid). Includes `file_uploaded_by_id` / `file_uploaded_by` when job.refId links to file_upload.
     * Requires JOB_VIEW or RECON_VIEW.
     */
    getById: { url: api_url + 'api/v1/jobs/:id', method: 'GET' },
    /**
     * GET this job's status-transition history (paginated).
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     */
    listTransitions: { url: api_url + 'api/v1/jobs/:id/transitions', method: 'GET' },
    /**
     * GET files associated with this job (paginated) — `PagedResponseJobFileResponse`.
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     * Each row includes fileSize and checksum when stored.
     */
    listFiles: { url: api_url + 'api/v1/jobs/:id/files', method: 'GET' },
    /**
     * GET mailbox mail JSON + sibling attachments for this job (MAILBOX channel).
     * Path `:id` (job uuid). Linked by ref_id; content always read from S3.
     * Empty when not MAILBOX or nothing indexed. Requires JOB_VIEW or RECON_VIEW.
     */
    listMailboxArtifacts: { url: api_url + 'api/v1/jobs/:id/mailbox-artifacts', method: 'GET' },
    /**
     * GET this job's detailed event log (paginated).
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     */
    listEvents: { url: api_url + 'api/v1/jobs/:id/events', method: 'GET' },
    /**
     * GET job run statistics for this job (paginated).
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     * One row per match_status for the job; job must exist (404 otherwise). Requires RECON_VIEW.
     */
    listStatistics: { url: api_url + 'api/v1/jobs/:id/job-run-stats', method: 'GET' },
    /**
     * GET reconciliation runs for this job (paginated).
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     * Uses reconciliation_run.job_id; job must exist (404 otherwise).
     */
    listReconciliationRuns: { url: api_url + 'api/v1/jobs/:id/reconciliation-runs', method: 'GET' },
    /**
     * GET comparison positions for this job (paginated) — `PagedResponsePositionResponse`.
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`),
     * optional `matchStatus` = exact stored value
     * (MATCHED, MATCHED_*_TOLERANCE, MATCHED_LOT_RELIEF, MISSING_IN_INTERNAL,
     * MISSING_IN_EXTERNAL, QTY_MISMATCH, MARKET_PRICE_MISMATCH, VALUE_MISMATCH),
     * `UNMATCHED`, or omit/`ALL`. Uses denormalized positions.job_id.
     * The prior run-scoped list (`GET /api/v1/reconciliation-runs/:id/positions`)
     * is not on the current Swagger spec.
     */
    listPositions: { url: api_url + 'api/v1/jobs/:id/positions', method: 'GET' },
    /**
     * GET column set and search fields for this job's Positions grid.
     * Path `:id` (uuid). Profile comes from tenant `ui.results_grid`, with the
     * workflow profile overriding the global one. Does not change `listPositions`.
     * Requires JOB_VIEW or RECON_VIEW.
     */
    resultsGrid: { url: api_url + 'api/v1/jobs/:id/results-grid', method: 'GET' },
    /**
     * GET download all comparison positions for this job as CSV (synchronous attachment).
     * Path `:id` (uuid). Exports every positions column (signed diffs, tenant/audit/AI/workflow fields).
     * Header-only file when matching has not written rows yet. Requires RECON_VIEW.
     */
    exportPositions: { url: api_url + 'api/v1/jobs/:id/positions/export', method: 'GET' },
    /**
     * POST request an async position CSV export for this job.
     * Path `:id` (uuid). Optional query `resultScope` = `ALL` | `MATCHED` | `BREAKS`
     * (default ALL when omitted). Creates a PENDING export request processed in the background.
     * Returns an existing in-flight request (PENDING/RUNNING) for the same user, job, and resultScope.
     * Poll GET `/api/v1/position-exports`; download via GET `/api/v1/position-exports/:id/download-url`. Requires RECON_VIEW.
     */
    requestPositionExport: { url: api_url + 'api/v1/jobs/:id/position-exports', method: 'POST' },
    /**
     * POST retry a failed job.
     * Path `:id` (uuid). Only legal when current status is FAILED; moves job back to RECEIVED and re-enters the pipeline.
     */
    retry: { url: api_url + 'api/v1/jobs/:id/retry', method: 'POST' },
  },
  jobFiles: {
    /** Get a job file by id — `JobFileResponse` (includes fileSize, checksum when stored) */
    getById: { url: api_url + 'api/v1/job-files/:id', method: 'GET' },
    /**
     * View MARKER file content inline — `JobFileContentResponse`
     * (`sourceType=MARKER`, typically package.manifest.json). Non-MARKER → use `downloadUrl` / `download`.
     */
    viewContent: { url: api_url + 'api/v1/job-files/:id/content', method: 'GET' },
    /**
     * GET issue a pre-signed S3 download URL — `JobFileDownloadUrlResponse`.
     * Path `:id` (uuid). Preferred for large S3-backed files; client GETs `downloadUrl` directly from S3.
     * Archive-member and local paths must use `download` instead. Requires JOB_VIEW or RECON_VIEW.
     */
    downloadUrl: { url: api_url + 'api/v1/job-files/:id/download', method: 'GET' },
    /**
     * GET download a non-MARKER job file through this API (binary stream + Content-Disposition).
     * Path `:id` (uuid). Use for archive-member / local paths; prefer `downloadUrl` for large S3 files.
     * Requires JOB_VIEW or RECON_VIEW.
     */
    download: { url: api_url + 'api/v1/job-files/:id/download', method: 'GET' },
    /** List this file's status-transition history (paginated; query: page, size, sort) */
    listTransitions: { url: api_url + 'api/v1/job-files/:id/transitions', method: 'GET' },
  },
  /**
   * Mailbox mail-drop artifacts — metadata.json + sibling attachments.
   * Content always read from S3 (never DB-cached). Requires JOB_VIEW or RECON_VIEW.
   */
  mailboxMails: {
    /** View metadata.json inline (S3) — `MailboxContentResponse` */
    viewContent: { url: api_url + 'api/v1/mailbox-mails/:id/content', method: 'GET' },
    /** Pre-signed S3 download URL for metadata.json */
    downloadUrl: { url: api_url + 'api/v1/mailbox-mails/:id/download', method: 'GET' },
  },
  mailboxAttachments: {
    /** View JSON attachment inline (S3) — non-JSON must use download */
    viewContent: { url: api_url + 'api/v1/mailbox-attachments/:id/content', method: 'GET' },
    /** Pre-signed S3 download URL for an attachment / package ZIP */
    downloadUrl: { url: api_url + 'api/v1/mailbox-attachments/:id/download', method: 'GET' },
  },
  /**
   * Reconciliation results across jobs — Swagger tag `Reconciliation`. Requires RECON_VIEW.
   * One tenant per call: `tenantId` is required for PLATFORM scope; optional `jobId` pins one job.
   * Shared filters: client, fund, omsSystem, workflowName, street, custodian, fileType, productType,
   * account, subAccount, assetClass, currency, matchStatus, reconType, jobType, jobStatus,
   * tradeDateFrom/To, positionDateFrom/To. BLANK selects null/blank values.
   */
  reconciliation: {
    /** GET positions across jobs (paginated) — `PagedResponsePositionResponse`. */
    positions: { url: api_url + 'api/v1/reconciliation/positions', method: 'GET' },
    /**
     * GET counts — `ReconciliationSummaryResponse` (`total`, `matched`, `unmatched`, `byMatchStatus`, `buckets`).
     * Optional `groupBy` returns one bucket per value. `total` honors matchStatus; the rest ignore it.
     */
    summary: { url: api_url + 'api/v1/reconciliation/summary', method: 'GET' },
    /**
     * GET distinct values for one `level` — `ReconciliationFacetResponse` (`values`, `blank`, `truncated`).
     * Every other filter sent narrows the list; the level's own filter is ignored. Up to 500 values.
     */
    facets: { url: api_url + 'api/v1/reconciliation/facets', method: 'GET' },
    /**
     * POST request an async position CSV export across jobs for one tenant.
     * Query: `tenantId`, the same filters as GET positions (client, fund, custodian, reconType, ...),
     * and optional `resultScope` = `ALL` | `MATCHED` | `BREAKS` (default ALL).
     * Returns an existing in-flight request for the same user, scope, and filters.
     * Poll GET `/api/v1/position-exports`; download via GET `/api/v1/position-exports/:id/download-url`. Requires RECON_VIEW.
     */
    requestPositionExport: { url: api_url + 'api/v1/reconciliation/position-exports', method: 'POST' },
  },
  reconciliationRuns: {
    /** List reconciliation runs (paginated) */
    list: { url: api_url + 'api/v1/reconciliation-runs', method: 'GET' },
    /** Get a reconciliation run by id */
    getById: { url: api_url + 'api/v1/reconciliation-runs/:id', method: 'GET' },
    /**
     * Not listed on the current Swagger spec (no `/api/v1/reconciliation-runs` paths).
     * Use `jobs.listPositions` / `positions.list` instead.
     * Retained so existing callers do not break.
     */
    listPositions: { url: api_url + 'api/v1/reconciliation-runs/:id/positions', method: 'GET' },
    /** List files that fed this run (paginated; CUSTODIAN / BROKER / OTHER job files) */
    listFiles: { url: api_url + 'api/v1/reconciliation-runs/:id/files', method: 'GET' },
    /** List match-status statistics for this run (paginated) */
    listStatistics: { url: api_url + 'api/v1/reconciliation-runs/:id/statistics', method: 'GET' },
  },
  reconciliationRunStatistics: {
    /** Get reconciliation run statistics by id */
    getById: { url: api_url + 'api/v1/reconciliation-run-statistics/:id', method: 'GET' },
  },
  /**
   * Positions — Swagger tag `Positions`
   * (`https://api.armatech.ai/swagger-ui/index.html#/Positions`).
   * Custodian/broker comparison rows and exception workflow (single + bulk resolve / audit).
   * Responses: `PositionResponse` / `PagedResponsePositionResponse` /
   * `PositionAuditLogResponse` / `PagedResponsePositionAuditLogResponse` /
   * `BulkResolvePositionsResponse` / `BulkResolvePositionsCountResponse`.
   * Job-scoped list lives on `jobs.listPositions` (Swagger tag Jobs); `positions.list` is an alias.
   */
  positions: {
    /**
     * GET list comparison positions for a job — `PagedResponsePositionResponse`.
     * Path `:id` (job uuid). Query: pageable (`page`, `size`, `sort`),
     * optional `matchStatus` = exact stored value
     * (MATCHED, MATCHED_*_TOLERANCE, MATCHED_LOT_RELIEF, MISSING_IN_INTERNAL,
     * MISSING_IN_EXTERNAL, QTY_MISMATCH, MARKET_PRICE_MISMATCH, VALUE_MISMATCH),
     * `UNMATCHED` (non-exact / non-tolerance matches), or omit/`ALL` for all rows.
     * Alias of `jobs.listPositions` for callers using the Positions module key.
     */
    list: { url: api_url + 'api/v1/jobs/:id/positions', method: 'GET' },
    /**
     * GET position by id — `PositionResponse`.
     * Path `:id` (uuid). TENANT scope = caller's tenant; PLATFORM-scoped RECON_VIEW = cross-tenant.
     */
    getById: { url: api_url + 'api/v1/positions/:id', method: 'GET' },
    /**
     * PATCH update exception workflow for one position —
     * body `ResolvePositionRequest` → `PositionResponse`.
     * Path `:id` (uuid). Required body: `status` = INVESTIGATING | RESOLVED | IGNORED.
     * Accept AI: `acceptAi=true` (or RESOLVED with no `resolutionCategoryCode`) copies
     * `aiSuggestedResolutionCategory` (400 if null). Override: send `resolutionCategoryCode`
     * from `lookups.listResolutionCategories?matchStatus=` (position's matchStatus).
     * Category codes are catalog-driven (not a fixed API enum).
     * QTY_MISMATCH + TRUST_INTERNAL / TRUST_EXTERNAL copy quantities between sides;
     * VALUE_MISMATCH does the same for values. Not valid for MATCHED rows.
     * Override `notes` → `resolutionNotes`; Accept ignores notes. `alerts` unchanged.
     */
    resolve: { url: api_url + 'api/v1/positions/:id/resolve', method: 'PATCH' },
    /**
     * GET field-level breaks for one position — `PositionBreakResponse[]`.
     * Path `:id` (position uuid). Authority `RECON_VIEW`.
     * Empty when the position has no `position_break` children.
     */
    listBreaks: { url: api_url + 'api/v1/positions/:id/breaks', method: 'GET' },
    /**
     * POST resolve one or more independent field breaks on one position —
     * body `ResolvePositionBreaksRequest` → `ResolvePositionBreaksResponse`.
     * Path `:id` (position uuid). Each item: `breakId`, `status`, optional
     * `resolutionCategoryCode`, `notes`, `acceptAi`. Dependents are rejected.
     */
    resolveFieldBreaks: { url: api_url + 'api/v1/positions/:id/breaks/resolve', method: 'POST' },
    /**
     * GET count open positions that would be bulk-resolved —
     * `BulkResolvePositionsCountResponse` (`count`, `jobId`, `matchStatus`).
     * Preview for filter mode before `POST /api/v1/positions/resolve`.
     * Query (required): `jobId` (uuid), `matchStatus` = exact break type
     * (e.g. QTY_MISMATCH, MISSING_IN_INTERNAL).
     * Counts open exception workflow rows (OPEN / INVESTIGATING;
     * excludes RESOLVED / IGNORED). ALL / UNMATCHED / MATCHED* are rejected.
     */
    countForBulkResolve: { url: api_url + 'api/v1/positions/resolve/count', method: 'GET' },
    /**
     * POST resolve multiple position breaks — body `BulkResolvePositionsRequest`
     * → `BulkResolvePositionsResponse` (`requested`, `resolved`, `failed`, `results[]`).
     * Modes (exactly one):
     * (1) `positionIds` — resolve the listed positions; optional `jobId` rejects ids
     *     that belong to another job.
     * (2) `jobId` + `matchStatus` — resolve every open break of that exact type
     *     (e.g. MISSING_IN_INTERNAL, QTY_MISMATCH); ALL / UNMATCHED / MATCHED* are rejected.
     * Required body: `status` = INVESTIGATING | RESOLVED | IGNORED.
     * RESOLVED requires `resolutionCategoryCode` from `lookups.listResolutionCategories`
     * (`matchStatus` = break type). Optional `notes`. Accept AI is single-only (not bulk).
     * Cap: 500 positions. Failures are isolated and reported per id.
     */
    resolveBulk: { url: api_url + 'api/v1/positions/resolve', method: 'POST' },
    /**
     * GET position audit history (paginated) — `PagedResponsePositionAuditLogResponse`.
     * Path `:id` (uuid). Query: pageable (`page`, `size`, `sort`).
     * Append-only trail (resolve, re-match, delete) with old/new JSON snapshots.
     * `username` = login email; `fullName` = display name at write time;
     * `changes` = field-level diff (`field`, `displayName`, `oldValue`, `newValue`).
     */
    listAudit: { url: api_url + 'api/v1/positions/:id/audit', method: 'GET' },
  },
  /**
   * Position Exports — Swagger tag `Position Exports`.
   * Async job position CSV export requests for the current user.
   * Create via `jobs.requestPositionExport` (POST `/api/v1/jobs/:id/position-exports`).
   * Responses: `PositionExportRequestResponse` /
   * `PagedResponsePositionExportRequestResponse` /
   * `PositionExportDownloadUrlResponse`.
   * Requires RECON_VIEW except `downloadUrlByToken` (public token redeem).
   */
  positionExports: {
    /**
     * GET list my position export requests — `PagedResponsePositionExportRequestResponse`.
     * Filtered to requested_by_id = current user (and tenant).
     * Query: optional `jobId` (uuid), pageable (`page`, `size`, `sort[]`).
     */
    list: { url: api_url + 'api/v1/position-exports', method: 'GET' },
    /**
     * Not listed on the current Swagger spec (tag `Position Exports`).
     * Was GET `/api/v1/position-exports/:id` → `PositionExportRequestResponse`.
     * Retained so existing callers do not break; use `list` and `downloadUrl`.
     */
    getById: { url: api_url + 'api/v1/position-exports/:id', method: 'GET' },
    /**
     * GET pre-signed S3 download URL for a completed export — `PositionExportDownloadUrlResponse`.
     * Path `:id` (uuid). Same pattern as `jobFiles.downloadUrl`.
     * Only COMPLETED exports owned by the caller. Client GETs `downloadUrl` from S3 directly.
     */
    downloadUrl: { url: api_url + 'api/v1/position-exports/:id/download-url', method: 'GET' },
    /**
     * GET pre-signed S3 download URL using an emailed export token — `PositionExportDownloadUrlResponse`.
     * Query (required): `token` (string). Public (no JWT); redeems the ready-email Open in Arma link.
     * Same body as `downloadUrl`. Invalid or expired tokens are rejected.
     */
    downloadUrlByToken: { url: api_url + 'api/v1/position-exports/download-url', method: 'GET' },
  },
  /**
   * Audit Logs — Swagger tag `Audit Logs`.
   * Platform mutation trail (`audit_log`). Filter by target entity.
   * Responses: `AuditLogResponse` / `PagedResponseAuditLogResponse`
   * (`changes`: `AuditFieldChange[]` — field / displayName / oldValue / newValue;
   *  actor: `userId`, `username`).
   */
  auditLogs: {
    /**
     * GET list audit changes for an entity — `PagedResponseAuditLogResponse`.
     * Query (required): `entityName` (string), `entityId` (string),
     * pageable (`page`, `size`, `sort`).
     */
    listByEntity: { url: api_url + 'api/v1/audit-logs', method: 'GET' },
    /**
     * GET one audit log row by id — `AuditLogResponse`.
     * Path `:id` (uuid).
     */
    getById: { url: api_url + 'api/v1/audit-logs/:id', method: 'GET' },
  },
  public: {
    /** Compute file checksum and byte size (not listed on current Swagger; retained for upload helpers) */
    checksum: { url: api_url + 'api/v1/public/checksum', method: 'POST' },
  },
};


